import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import saveSurveyResult from '../utils/saveSurveyResult'
import createSurveyFaceRecordingManager from '../utils/createSurveyFaceRecordingManager'
import './genZMkSurvey.css'

const SURVEY_CODE = 'gen_z_me_neuro'
const ADS = [
  { id: 'niksicko', name: 'Reklama 1', url: 'https://ai.surveyneuro.com/images/7-Niksicko.mp4' },
  { id: 'telecom', name: 'Reklama 2', url: 'https://ai.surveyneuro.com/images/8-Telecom.mp4' },
]
const POINTS = [
  [10, 25], [50, 25], [90, 25],
  [10, 55], [50, 55], [90, 55],
  [10, 85], [50, 85], [90, 85],
]
const CLICKS_PER_POINT = 5
const IMPLICIT_DEADLINE_MS = 1800
const IMPLICIT_ATTRIBUTES = [
  { label: 'Autentičan', words: ['Autentičan', 'Iskren', 'Istinit'] },
  { label: 'U trendu', words: ['U trendu', 'Savremen', 'Inovativan'] },
  { label: 'Pouzdan', words: ['Pouzdan', 'Verodostojan', 'Siguran'] },
]
const PLATFORMS = ['TikTok', 'Instagram', 'YouTube', 'Snapchat', 'Twitch', 'X']
const TOPICS = ['Mimove', 'Uputstva/„uradi sam“', 'Video-igre', 'Fitnes', 'Moda/ljepota', 'Tehnologija', 'Vijesti', 'Politika/aktivizam', 'Hrana']
const SOURCES = ['Prijatelji', 'Influenseri', 'Kanali brendova', 'Novinari/Mediji', 'Sajtovi sa recenzijama']
const AD_QUESTIONS = [
  'Ova reklama mi je bila zanimljiva.',
  'Poruka je bila jasna.',
  'Reklama je djelovala iskreno/autentično.',
  'Ova reklama me je emotivno dotakla.',
  'Ova reklama je povećala moje povjerenje u brend.',
  'Poslije gledanja reklame, veća je vjerovatnoća da ću kupiti/isprobati reklamirani proizvod ili uslugu.',
]
const BELIEF_QUESTIONS = [
  'Održivost utiče na moje odluke o kupovini.',
  'Više volim brendove koji zauzimaju stav o društvenim pitanjima.',
  'Preporuke influensera su pouzdane.',
  'Brine me kako brendovi koriste moje podatke.',
]
const choice = (value, choices) => choices.includes(value)
const shuffle = (items) => [...items].sort(() => Math.random() - 0.5)
const makeResponseId = () => `me-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
const average = (values) => values.length ? Math.round(values.reduce((sum, value) => sum + value, 0) / values.length) : null

function summarizeImplicit(trials) {
  const valid = trials.filter((trial) => !trial.timed_out)
  return {
    trial_count: trials.length,
    on_time_count: valid.length,
    mean_on_time_rt_ms: average(valid.map((trial) => trial.rt_ms)),
    attributes: Object.fromEntries(IMPLICIT_ATTRIBUTES.map((attribute) => {
      const group = trials.filter((trial) => trial.attribute === attribute.label)
      const onTime = group.filter((trial) => !trial.timed_out)
      return [attribute.label, {
        match_count: group.filter((trial) => trial.response === 'matches').length,
        trial_count: group.length,
        mean_on_time_rt_ms: average(onTime.map((trial) => trial.rt_ms)),
      }]
    })),
  }
}

function Scale({ questions, answers, onChange, prefix }) {
  return <div className="mk-matrix">
    <p className="mk-help">1 = Uopšte se ne slažem · 7 = U potpunosti se slažem</p>
    {questions.map((question, index) => <fieldset key={question} className="mk-fieldset">
      <legend>{prefix}{questions.length === 1 ? '' : index + 1}. {question}</legend>
      <div className="mk-scale">{[1, 2, 3, 4, 5, 6, 7].map((number) => <label key={number} className={Number(answers[index]) === number ? 'selected' : ''}><input type="radio" name={`${prefix}-${index}`} checked={Number(answers[index]) === number} onChange={() => onChange(index, number)} />{number}</label>)}</div>
    </fieldset>)}
  </div>
}

export default function GenZMeSurvey() {
  const responseId = useMemo(() => new URLSearchParams(window.location.search).get('responseId') || makeResponseId(), [])
  const adOrder = useMemo(() => shuffle(ADS), [])
  const faceRecording = useMemo(() => createSurveyFaceRecordingManager({ surveyCode: SURVEY_CODE, responseId }), [responseId])
  const implicitTrialSets = useMemo(() => adOrder.map(() => shuffle(IMPLICIT_ATTRIBUTES.flatMap((attribute, attributeIndex) => attribute.words.map((word) => ({ attribute: attribute.label, attribute_index: attributeIndex + 1, stimulus: word }))))), [adOrder])
  const [phase, setPhase] = useState('consent')
  const [consent, setConsent] = useState({ age: '', camera: '', privacy: '' })
  const [demographics, setDemographics] = useState({ country: 'ME', age: '', gender: '', population: '', status: '' })
  const [habits, setHabits] = useState({ minutes: {}, shortVideos: '', topics: [], socialPurchase: '', onlinePercent: 50 })
  const [adIndex, setAdIndex] = useState(0)
  const [videoStarted, setVideoStarted] = useState(false)
  const [adAnswers, setAdAnswers] = useState({ scales: {}, share: '', impression: '' })
  const [adResults, setAdResults] = useState([])
  const [implicitIndex, setImplicitIndex] = useState(0)
  const [implicitResults, setImplicitResults] = useState([])
  const [implicitLate, setImplicitLate] = useState(false)
  const [beliefs, setBeliefs] = useState({ scales: {}, ranking: SOURCES })
  const [error, setError] = useState('')
  const [cameraState, setCameraState] = useState('idle')
  const [faceSeen, setFaceSeen] = useState(false)
  const [calibrationCounts, setCalibrationCounts] = useState(POINTS.map(() => 0))
  const [recalibration, setRecalibration] = useState(false)
  const trackerRef = useRef(null)
  const startedRef = useRef(false)
  const samplesRef = useRef([])
  const exposureRef = useRef(null)
  const exposuresRef = useRef([])
  const needsRecalibrationRef = useRef(false)
  const calibrationRef = useRef([])
  const videoRef = useRef(null)
  const implicitStartRef = useRef(0)
  const implicitLockedRef = useRef(false)
  const implicitTimerRef = useRef(null)
  const currentAd = adOrder[adIndex]
  const currentImplicitTrials = implicitTrialSets[adIndex]

  const stopTracker = useCallback(() => {
    faceRecording.abort()
    if (!startedRef.current) return
    try { trackerRef.current?.clearGazeListener() } catch (e) { console.warn(e) }
    try { trackerRef.current?.stopVideo() } catch (e) { console.warn(e) }
    try { trackerRef.current?.end() } catch (e) { console.warn(e) }
    startedRef.current = false
  }, [faceRecording])
  useEffect(() => () => stopTracker(), [stopTracker])
  useEffect(() => () => window.clearTimeout(implicitTimerRef.current), [])

  useEffect(() => {
    if (phase !== 'neutral') return undefined
    const timer = window.setTimeout(() => setPhase('ad-intro'), 3000)
    return () => window.clearTimeout(timer)
  }, [phase])

  useEffect(() => {
    if (phase !== 'implicit-stimulus') return undefined
    implicitLockedRef.current = false
    implicitStartRef.current = performance.now()
    setImplicitLate(false)
    const deadline = window.setTimeout(() => setImplicitLate(true), IMPLICIT_DEADLINE_MS)
    return () => window.clearTimeout(deadline)
  }, [phase, implicitIndex])

  const beginCamera = async (isRecalibration = false) => {
    setError('')
    setRecalibration(isRecalibration)
    setCalibrationCounts(POINTS.map(() => 0))
    setCameraState('loading')
    setPhase('camera-check')
    try {
      const module = await import('../webgazer/webgazer')
      const tracker = module.default
      trackerRef.current = tracker
      if (!tracker.detectCompatibility()) throw new Error('unsupported camera')
      if (!startedRef.current) {
        tracker.saveDataAcrossSessions(false).setRegression('ridge').setTracker('TFFacemesh').applyKalmanFilter(true).showVideoPreview(true).showPredictionPoints(false).setGazeListener((data) => {
          const face = tracker.getTracker()?.getPositions?.()
          setFaceSeen(Array.isArray(face) && face.length > 0)
          const exposure = exposureRef.current
          if (!exposure || !data || !Number.isFinite(data.x) || !Number.isFinite(data.y) || samplesRef.current.length >= 5000) return
          samplesRef.current.push({ x: Math.round(data.x * 100) / 100, y: Math.round(data.y * 100) / 100, t_ms: Math.round(performance.now() - exposure.performanceStart) })
        })
        await tracker.clearData()
        startedRef.current = true
        await tracker.begin()
        tracker.removeMouseEventListeners()
      } else {
        await tracker.resume()
      }
      tracker.setVideoViewerSize(300, 225)
      tracker.showVideoPreview(true)
      setCameraState('ready')
    } catch (e) {
      stopTracker()
      setCameraState('error')
      setError('Kamera ili praćenje pogleda ne mogu da se pokrenu. Provjerite dozvolu za kameru i pokušajte ponovo.')
    }
  }

  const startCalibration = () => {
    trackerRef.current?.showVideoPreview(false).showPredictionPoints(false)
    setPhase('calibration')
  }

  const clickPoint = (index, event) => {
    if (!faceSeen || calibrationCounts[index] >= CLICKS_PER_POINT) return
    const box = event.currentTarget.getBoundingClientRect()
    trackerRef.current?.recordScreenPosition(box.left + box.width / 2, box.top + box.height / 2, 'click')
    const next = [...calibrationCounts]
    next[index] += 1
    setCalibrationCounts(next)
    if (next.every((count) => count >= CLICKS_PER_POINT)) {
      calibrationRef.current.push({ completed_at: new Date().toISOString(), points: POINTS.length, clicks_per_point: CLICKS_PER_POINT, after_ad: recalibration ? adIndex : null })
      trackerRef.current?.showVideoPreview(false).showPredictionPoints(false)
      setCameraState('done')
      setPhase(recalibration ? 'neutral' : 'demographics')
    }
  }

  const validateConsent = () => {
    if (Object.values(consent).some((answer) => !answer)) return setError('Odgovorite na sva obavezna pitanja.')
    if (Object.values(consent).some((answer) => answer === 'no')) return setPhase('ineligible')
    beginCamera()
  }
  const validateDemographics = () => {
    if (!demographics.age || !demographics.gender || !demographics.population || !demographics.status) return setError('Popunite sva pitanja.')
    const age = Number(demographics.age)
    if (!Number.isInteger(age) || age < 18 || age > 29) return setError('Ovo istraživanje je namenjeno osobama od 18 do 29 godina.')
    setError(''); setPhase('habits')
  }
  const validateHabits = () => {
    if (PLATFORMS.some((platform) => habits.minutes[platform] === undefined || habits.minutes[platform] === '') || !habits.shortVideos || !habits.socialPurchase) return setError('Popunite sva obavezna pitanja.')
    if (PLATFORMS.some((platform) => Number(habits.minutes[platform]) < 0 || Number(habits.minutes[platform]) > 1440)) return setError('Unesite od 0 do 1440 minuta za svaku platformu.')
    setError(''); setPhase('ad-intro')
  }
  const beginAd = async () => {
    setError('')
    try {
      await trackerRef.current?.resume?.()
      faceRecording.prepare()
      setVideoStarted(false)
      setPhase('ad')
    } catch (e) { setError('Praćenje pogleda ne može da se nastavi. Pokušajte ponovo.') }
  }
  const onAdPlay = () => {
    if (exposureRef.current) return
    try { faceRecording.start() } catch (recordingError) {
      videoRef.current?.pause()
      faceRecording.abort()
      setError('Snimanje kamerom ne može da počne. Provjerite dozvolu za kameru i pokušajte ponovo.')
      setPhase('ad-intro')
      return
    }
    setVideoStarted(true)
    samplesRef.current = []
    exposureRef.current = { advertisement_id: currentAd.id, advertisement_url: currentAd.url, started_at: new Date().toISOString(), performanceStart: performance.now(), viewport: { width: window.innerWidth, height: window.innerHeight } }
  }
  const finishAd = async () => {
    const exposure = exposureRef.current
    if (!exposure) return
    exposureRef.current = null
    const durationMs = Math.round(performance.now() - exposure.performanceStart)
    needsRecalibrationRef.current = samplesRef.current.length < Math.max(10, Math.ceil(durationMs / 1000) * 2)
    const exposureResult = { advertisement_id: exposure.advertisement_id, advertisement_number: adIndex + 1, advertisement_url: exposure.advertisement_url, started_at: exposure.started_at, ended_at: new Date().toISOString(), duration_ms: durationMs, viewport: exposure.viewport, gaze_samples: samplesRef.current }
    faceRecording.finish(exposureResult)
    trackerRef.current?.pause?.()
    setPhase('recording-saving')
    try {
      await faceRecording.ensureUploaded([exposureResult])
      exposuresRef.current.push(exposureResult)
      setPhase('implicit-intro')
    } catch (recordingError) {
      setError('Snimak sa kamere nije mogao da se sačuva. Provjerite vezu i ponovo pustite reklamu.')
      setPhase('ad-intro')
    }
  }
  const showImplicitTrial = () => {
    setPhase('implicit-fixation')
    implicitTimerRef.current = window.setTimeout(() => setPhase('implicit-stimulus'), 300)
  }
  const beginImplicitTest = () => {
    setImplicitIndex(0)
    setImplicitResults([])
    showImplicitTrial()
  }
  const respondImplicit = (response) => {
    if (phase !== 'implicit-stimulus' || implicitLockedRef.current) return
    implicitLockedRef.current = true
    const rtMs = Math.round(performance.now() - implicitStartRef.current)
    setImplicitResults((previous) => [...previous, { ...currentImplicitTrials[implicitIndex], response, rt_ms: rtMs, timed_out: rtMs > IMPLICIT_DEADLINE_MS }])
    setPhase('implicit-gap')
    implicitTimerRef.current = window.setTimeout(() => {
      if (implicitIndex + 1 >= currentImplicitTrials.length) setPhase('ad-questions')
      else { setImplicitIndex(implicitIndex + 1); showImplicitTrial() }
    }, 300)
  }
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.code === 'KeyE') respondImplicit('does_not_match')
      if (event.code === 'KeyI') respondImplicit('matches')
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  })
  const saveAdQuestions = () => {
    if (Object.keys(adAnswers.scales).length !== AD_QUESTIONS.length || !choice(adAnswers.share, ['yes', 'no']) || !adAnswers.impression.trim()) return setError('Odgovorite na sva pitanja o reklami.')
    setAdResults((previous) => [...previous, { advertisement_id: currentAd.id, advertisement_url: currentAd.url, answers: adAnswers, implicit_trials: implicitResults, implicit_summary: summarizeImplicit(implicitResults) }])
    setAdAnswers({ scales: {}, share: '', impression: '' }); setError('')
    if (adIndex + 1 < adOrder.length) {
      setAdIndex(adIndex + 1)
      if (needsRecalibrationRef.current) { needsRecalibrationRef.current = false; beginCamera(true) }
      else setPhase('neutral')
    }
    else setPhase('beliefs')
  }
  const moveRanking = (index, direction) => {
    const target = index + direction
    if (target < 0 || target >= beliefs.ranking.length) return
    const ranking = [...beliefs.ranking]
    ;[ranking[index], ranking[target]] = [ranking[target], ranking[index]]
    setBeliefs((previous) => ({ ...previous, ranking }))
  }
  const save = async () => {
    if (Object.keys(beliefs.scales).length !== BELIEF_QUESTIONS.length) return setError('Odgovorite na sve tvrdnje.')
    setError(''); setPhase('saving')
    try {
      stopTracker()
      if (exposuresRef.current.length !== adOrder.length) throw new Error('Missing advertisement exposure')
      await faceRecording.ensureUploaded(exposuresRef.current)
      const answerLabels = {
        'Uzrast': demographics.age,
        'Rod': demographics.gender,
        'Mjesto stanovanja': demographics.population,
        'Status': demographics.status,
        ...Object.fromEntries(PLATFORMS.map((platform) => [`H1 ${platform} (minuti)`, habits.minutes[platform]])),
        'H2 Kratki video-snimci': habits.shortVideos,
        'H3 Sadržaji': habits.topics,
        'H4 Kupovina preko društvenih mreža': habits.socialPurchase === 'yes' ? 'Da' : 'Ne',
        'H5 Onlajn kupovine (%)': habits.onlinePercent,
        ...Object.fromEntries(adResults.flatMap((ad) => [
          ...AD_QUESTIONS.map((question, index) => [`${ad.advertisement_id} A${index + 1} ${question}`, ad.answers.scales[index]]),
          [`${ad.advertisement_id} A7 Dijeljenje`, ad.answers.share === 'yes' ? 'Da' : 'Ne'],
          [`${ad.advertisement_id} A8 Utisak`, ad.answers.impression],
          ...IMPLICIT_ATTRIBUTES.map((attribute) => [`${ad.advertisement_id} Implicitno: ${attribute.label} (poklapanja)`, ad.implicit_trials.filter((trial) => trial.attribute === attribute.label && trial.response === 'matches').length]),
          [`${ad.advertisement_id} Implicitno: prosečno vreme (ms)`, ad.implicit_summary.mean_on_time_rt_ms],
        ])),
        ...Object.fromEntries(BELIEF_QUESTIONS.map((question, index) => [`B${index + 1} ${question}`, beliefs.scales[index]])),
        'B5 Izvori prema povjerenju': beliefs.ranking,
      }
      const answers = {
        AGE: Number(demographics.age), GENDER: demographics.gender, POPULATION: demographics.population, STATUS: demographics.status,
        ...Object.fromEntries(PLATFORMS.map((platform) => [`H1_${platform.toUpperCase()}`, Number(habits.minutes[platform])])),
        H2: Number(habits.shortVideos), H3: habits.topics, H4: habits.socialPurchase, H5: habits.onlinePercent,
        ...Object.fromEntries(adResults.flatMap((ad) => [
          ...AD_QUESTIONS.map((_, index) => [`${ad.advertisement_id.toUpperCase()}_A${index + 1}`, Number(ad.answers.scales[index])]),
          [`${ad.advertisement_id.toUpperCase()}_A7`, ad.answers.share],
          [`${ad.advertisement_id.toUpperCase()}_A8`, ad.answers.impression],
          ...IMPLICIT_ATTRIBUTES.map((attribute, index) => [`${ad.advertisement_id.toUpperCase()}_IMP${index + 1}`, ad.implicit_trials.filter((trial) => trial.attribute === attribute.label && trial.response === 'matches').length]),
          [`${ad.advertisement_id.toUpperCase()}_RT`, ad.implicit_summary.mean_on_time_rt_ms],
        ])),
        ...Object.fromEntries(BELIEF_QUESTIONS.map((_, index) => [`B${index + 1}`, Number(beliefs.scales[index])])),
        B5: beliefs.ranking.join(' > '),
      }
      await saveSurveyResult({ surveyCode: SURVEY_CODE, responseId, result: {
        survey_code: SURVEY_CODE, response_id: responseId, status: 'completed', language: 'cnr', country: 'ME', completed_at: new Date().toISOString(),
        consent: { age_18_or_over: true, camera_tracking: true, camera_video_storage: true, privacy_policy: true }, demographics, media_habits: habits,
        advertisement_order: adOrder.map((ad) => ad.id), advertisement_tests: adResults, beliefs, answers, answer_labels: answerLabels,
        eye_tracking: { engine: 'webgazer', calibration: calibrationRef.current, ad_exposures: exposuresRef.current, emotion_analysis_status: 'not_available', aoi_status: 'not_defined' },
        implicit_test: { status: 'completed', paradigm: 'advertisement_attribute_match_rt', deadline_ms: IMPLICIT_DEADLINE_MS, attributes: IMPLICIT_ATTRIBUTES.map((attribute) => attribute.label), score_note: 'Response times and match choices are recorded; a conventional IAT D-score requires contrasted category blocks.' },
      } })
      setPhase('complete')
    } catch (e) { setError('Odgovori nijesu sačuvani. Provjerite internet vezu i pokušajte ponovo.'); setPhase('beliefs') }
  }

  const screen = (title, body, action, label) => <main className="mk-screen"><section><div className="mk-kicker">NEURO ANKETA · GENERACIJA Z</div><h1>{title}</h1>{body}{error && <p className="mk-error" role="alert">{error}</p>}{action && <button className="mk-primary" onClick={action}>{label || 'Nastavi'}</button>}</section></main>
  const yesNo = (name, value, onChange) => <div className="mk-options">{[['yes', 'Da'], ['no', 'Ne']].map(([key, label]) => <label key={key}><input type="radio" name={name} checked={value === key} onChange={() => onChange(key)} />{label}</label>)}</div>

  if (phase === 'consent') return screen('Saglasnost i priprema', <><p>Molimo pročitajte i odgovorite prije početka.</p>{[
    ['age', 'Imam 18 godina ili više.'],
    ['camera', 'Saglasan/na sam da se kamera mog računara koristi za praćenje pogleda i snimanje lica tokom prikazivanja reklama. Video sa kamere čuva se za kasniju istraživačku obradu, zajedno sa izvedenim koordinatama pogleda.'],
    ['privacy', 'Prihvatam politiku privatnosti i način postupanja s podacima.'],
  ].map(([key, text]) => <fieldset className="mk-fieldset" key={key}><legend>{text} <b>*</b></legend>{yesNo(key, consent[key], (value) => setConsent({ ...consent, [key]: value }))}</fieldset>)}</>, validateConsent, 'Nastavi')
  if (phase === 'ineligible') return screen('Hvala vam', <p>Ne možete da nastavite istraživanje na osnovu datih odgovora.</p>)
  if (phase === 'camera-check') return <main className="mk-screen mk-camera-check"><section><div className="mk-kicker">NEURO ANKETA · GENERACIJA Z</div><h1>Provjera kamere</h1><p>Postavite lice u centar slike. Obezbijedite dobro osvjetljenje i sjedite na 60–90 cm od ekrana.</p>{cameraState === 'loading' && <p>Kamera se uključuje…</p>}{cameraState === 'ready' && <><p className={faceSeen ? 'mk-face-ok' : 'mk-face-wait'}>{faceSeen ? 'Vaše lice je prepoznato. Možete da nastavite.' : 'Tražimo vaše lice u kadru…'}</p><div className="mk-camera-space" /><button className="mk-primary" disabled={!faceSeen} onClick={startCalibration}>Započni kalibraciju</button></>}{cameraState === 'error' && <><p className="mk-error">{error}</p><button className="mk-primary" onClick={() => beginCamera(recalibration)}>Pokušajte ponovo</button></>}</section></main>
  if (phase === 'calibration') return <main className="mk-calibration"><div className="mk-calibration-card"><h1>Podešavanje i kalibracija</h1><p>Obezbijedite dobro osvjetljenje, izbjegavajte naočare sa reflektujućim staklima, sjedite na 60–90 cm od ekrana i držite glavu mirno.</p>{cameraState === 'loading' && <p>Kamera se uključuje…</p>}{cameraState === 'error' && <><p className="mk-error">{error}</p><button onClick={() => beginCamera(recalibration)}>Pokušajte ponovo</button></>}{cameraState === 'ready' && <p>{faceSeen ? 'Kliknite pet puta na svaku tačku dok gledate u nju.' : 'Postavite lice u kadar kamere.'} · {calibrationCounts.reduce((a, b) => a + b, 0)}/{POINTS.length * CLICKS_PER_POINT}</p>}</div>{cameraState === 'ready' && POINTS.map(([x, y], index) => <button key={index} className="mk-point" style={{ left: `${x}%`, top: `${y}%` }} disabled={!faceSeen || calibrationCounts[index] >= CLICKS_PER_POINT} onClick={(event) => clickPoint(index, event)} aria-label={`Tačka ${index + 1}`}>{calibrationCounts[index] >= CLICKS_PER_POINT ? '✓' : CLICKS_PER_POINT - calibrationCounts[index]}</button>)}</main>
  if (phase === 'demographics') return screen('Početna pitanja i demografski podaci', <div className="mk-form"><label>Zemlja<input value="Crna Gora" readOnly /></label><label>Uzrast (18–29)<input type="number" min="18" max="29" value={demographics.age} onChange={(e) => setDemographics({ ...demographics, age: e.target.value })} /></label>{[
    ['gender', 'Rod', ['Ženski', 'Muški', 'Nebinarni', 'Ne želim da odgovorim']],
    ['population', 'Broj stanovnika u mjestu stanovanja', ['Manje od 50.000', '50.000–200.000', '200.000–1.000.000', 'Više od 1.000.000']],
    ['status', 'Status', ['Student/kinja', 'Zaposlen/a', 'Samozaposlen/a', 'Nezaposlen/a', 'Drugo']],
  ].map(([key, label, options]) => <label key={key}>{label}<select value={demographics[key]} onChange={(e) => setDemographics({ ...demographics, [key]: e.target.value })}><option value="">Izaberite</option>{options.map((option) => <option key={option}>{option}</option>)}</select></label>)}</div>, validateDemographics)
  if (phase === 'habits') return screen('Medijske navike', <div className="mk-form"><p>H1. Koliko minuta dnevno provodite na sljedećim platformama?</p><div className="mk-grid">{PLATFORMS.map((platform) => <label key={platform}>{platform}<input type="number" min="0" max="1440" value={habits.minutes[platform] ?? ''} onChange={(e) => setHabits({ ...habits, minutes: { ...habits.minutes, [platform]: e.target.value } })} /></label>)}</div><Scale questions={['Obično gledam kratke vertikalne video-snimke (Reels/TikTok).']} answers={{ 0: habits.shortVideos }} onChange={(_, value) => setHabits({ ...habits, shortVideos: value })} prefix="H2" /><fieldset className="mk-fieldset"><legend>H3. Redovno pratim (možete izabrati više odgovora).</legend><div className="mk-checks">{TOPICS.map((topic) => <label key={topic}><input type="checkbox" checked={habits.topics.includes(topic)} onChange={(e) => setHabits({ ...habits, topics: e.target.checked ? [...habits.topics, topic] : habits.topics.filter((item) => item !== topic) })} />{topic}</label>)}</div></fieldset><fieldset className="mk-fieldset"><legend>H4. U posljednjih 30 dana kupio/la sam nešto što sam prvi put vidio/vidjela na društvenim mrežama.</legend>{yesNo('socialPurchase', habits.socialPurchase, (value) => setHabits({ ...habits, socialPurchase: value }))}</fieldset><label>H5. Koliki procenat vaših kupovina obavljate onlajn? <strong>{habits.onlinePercent}%</strong><input type="range" min="0" max="100" value={habits.onlinePercent} onChange={(e) => setHabits({ ...habits, onlinePercent: Number(e.target.value) })} /></label></div>, validateHabits)
  if (phase === 'ad-intro') return screen('Reklame', <p>Pogledaćete {adOrder.length} reklame nasumičnim redosljedom. Poslije svake reklame slijedi kratki test poklapanja riječi, a zatim nekoliko pitanja. Gledajte u ekran i držite glavu mirno.</p>, beginAd, 'Prikaži reklamu')
  if (phase === 'neutral') return <main className="mk-neutral" aria-label="Pauza od tri sekunde"><span>+</span></main>
  if (phase === 'ad') return <main className="mk-video"><video ref={videoRef} src={currentAd.url} autoPlay playsInline onPlay={onAdPlay} onEnded={finishAd} onError={() => { faceRecording.abort(); exposureRef.current = null; setError('Reklama ne može da se učita. Pokušajte ponovo.'); setPhase('ad-intro') }} />{!videoStarted && <button className="mk-play" onClick={() => videoRef.current?.play()}>Pusti reklamu</button>}<span>Reklama {adIndex + 1} od {adOrder.length}</span></main>
  if (phase === 'implicit-intro') return screen('Kratki test reakcije', <><p>Za svaku riječ odlučite da li odgovara utisku koji je na vas ostavila reklama koju ste upravo gledali.</p><p>Odgovorite što brže i tačnije. Koristite dugmad na ekranu ili tastere <strong>E</strong> (ne poklapa se) i <strong>I</strong> (poklapa se).</p><p>Riječi su povezane sa: <strong>Autentičan, U trendu, Pouzdan.</strong></p></>, beginImplicitTest, 'Započni test')
  if (phase === 'implicit-fixation' || phase === 'implicit-gap') return <main className="mk-implicit-blank" aria-hidden="true">{phase === 'implicit-fixation' ? '+' : ''}</main>
  if (phase === 'implicit-stimulus') return <main className="mk-implicit"><div className="mk-implicit-progress">{implicitIndex + 1} / {currentImplicitTrials.length}</div><p>Da li riječ odgovara reklami?</p><h1>{currentImplicitTrials[implicitIndex].stimulus}</h1><div className="mk-implicit-actions"><button onClick={() => respondImplicit('does_not_match')}>NE POKLAPA SE <small>E</small></button><button onClick={() => respondImplicit('matches')}>POKLAPA SE <small>I</small></button></div><div className="mk-implicit-hint">{implicitLate ? 'Pokušajte brže da odgovorite na sljedeću riječ.' : 'Odgovorite brzo i tačno.'}</div></main>
  if (phase === 'ad-questions') return screen(`Pitanja poslije reklame ${adIndex + 1}`, <><Scale questions={AD_QUESTIONS} answers={adAnswers.scales} onChange={(index, value) => setAdAnswers({ ...adAnswers, scales: { ...adAnswers.scales, [index]: value } })} prefix="A" /><fieldset className="mk-fieldset"><legend>A7. Podijelio/la bih ovu reklamu sa prijateljima.</legend>{yesNo('share', adAnswers.share, (value) => setAdAnswers({ ...adAnswers, share: value }))}</fieldset><label className="mk-label">A8. Šta vam je najviše privuklo pažnju?<input maxLength="300" value={adAnswers.impression} onChange={(e) => setAdAnswers({ ...adAnswers, impression: e.target.value })} /></label></>, saveAdQuestions)
  if (phase === 'beliefs') return screen('Uvjerenja i stavovi', <><Scale questions={BELIEF_QUESTIONS} answers={beliefs.scales} onChange={(index, value) => setBeliefs({ ...beliefs, scales: { ...beliefs.scales, [index]: value } })} prefix="B" /><h2>B5. Poređajte izvore informacija prema povjerenju</h2><p>Izvor kojem najviše vjerujete postavite na prvo mjesto.</p><ol className="mk-ranking">{beliefs.ranking.map((source, index) => <li key={source}>{source}<span><button type="button" onClick={() => moveRanking(index, -1)} disabled={index === 0} aria-label={`${source} naviše`}>↑</button><button type="button" onClick={() => moveRanking(index, 1)} disabled={index === beliefs.ranking.length - 1} aria-label={`${source} naniže`}>↓</button></span></li>)}</ol></>, save, 'Završi')
  if (phase === 'recording-saving') return screen('Čuvanje snimka sa kamere', <p>Molimo sačekajte…</p>)
  if (phase === 'saving') return screen('Čuvanje odgovora', <p>Molimo sačekajte…</p>)
  return screen('Hvala vam', <p>Vaši odgovori su sačuvani.</p>)
}
